import { apiAxios } from '@/lib/apiAxios';
import { DATE_FORMAT_ISO } from '@/constants/date';
import { format } from 'date-fns';
export const fetchCampaigns = async (params) => apiAxios
    .get('/campaigns', {
    params: {
        status: params.status,
        name: params.name,
        'excludeDevices[]': params.excludeDevices,
        'excludeBrowsers[]': params.excludeBrowsers,
        from: params.from ? format(params.from, DATE_FORMAT_ISO) : undefined,
        to: params.to ? format(params.to, DATE_FORMAT_ISO) : undefined,
    },
})
    .then((response) => response.data);
export const postCampaign = async ({ data, imageFile, }) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) {
        formData.append('description', data.description);
    }
    // Applied to the default smart link the create endpoint builds — see
    // PartialCampaign.customSlug. Omitted when blank so the entry point keeps
    // its auto-generated `/c/ep-{publicId}` URL.
    if (data.customSlug && data.customSlug.trim() !== '') {
        formData.append('customSlug', data.customSlug.trim());
    }
    formData.append('imageFile', imageFile, imageFile.name);
    const mode = data.mode ?? (data.storeAutoDetectEnabled ? 'auto_store' : 'classic');
    formData.append('mode', mode);
    if (mode === 'auto_store') {
        formData.append('storeAutoDetectEnabled', '1');
        if (data.fallbackUrl) {
            formData.append('fallbackUrl', data.fallbackUrl);
        }
    }
    if (mode === 'feedback') {
        if (data.feedbackTitle)
            formData.append('feedbackTitle', data.feedbackTitle);
        if (data.feedbackThankYouMessage)
            formData.append('feedbackThankYouMessage', data.feedbackThankYouMessage);
        if (data.feedbackTelegramChatId)
            formData.append('feedbackTelegramChatId', data.feedbackTelegramChatId);
        formData.append('feedbackCollectName', data.feedbackCollectName === false ? '0' : '1');
        formData.append('feedbackCollectEmail', data.feedbackCollectEmail === false ? '0' : '1');
        // Custom questions are validated/sanitised server-side; send the whole
        // list as JSON (multipart can't carry nested arrays cleanly).
        formData.append('feedbackQuestions', JSON.stringify(data.feedbackQuestions ?? []));
    }
    if (mode === 'showroom') {
        if (data.showroomTagline)
            formData.append('showroomTagline', data.showroomTagline);
        if (data.showroomCurrency)
            formData.append('showroomCurrency', data.showroomCurrency);
        if (data.showroomCallNumber)
            formData.append('showroomCallNumber', data.showroomCallNumber);
        if (data.showroomWhatsappNumber)
            formData.append('showroomWhatsappNumber', data.showroomWhatsappNumber);
        if (data.showroomTelegramHandle)
            formData.append('showroomTelegramHandle', data.showroomTelegramHandle);
        if (data.showroomDirectionsUrl)
            formData.append('showroomDirectionsUrl', data.showroomDirectionsUrl);
    }
    if (data.createdFromTemplate) {
        formData.append('createdFromTemplate', data.createdFromTemplate);
    }
    if (mode !== 'feedback') {
        let index = 0;
        data.trackingLinks.forEach((link) => {
            if (link.targetUrl && link.label) {
                formData.append(`trackingLinks[${index}][targetUrl]`, link.targetUrl);
                formData.append(`trackingLinks[${index}][label]`, link.label);
                formData.append(`trackingLinks[${index}][type]`, link.type);
                if (link.context) {
                    formData.append(`trackingLinks[${index}][context]`, link.context);
                }
                if (link.description) {
                    formData.append(`trackingLinks[${index}][description]`, link.description);
                }
                if (link.priceLabel) {
                    formData.append(`trackingLinks[${index}][priceLabel]`, link.priceLabel);
                }
                if (link.imageFile instanceof File) {
                    formData.append(`trackingLinks[${index}][imageFile]`, link.imageFile, link.imageFile.name);
                }
                ++index;
            }
        });
    }
    const response = await apiAxios.post('/campaigns', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};
export const fetchSummary = async () => apiAxios.get('/campaigns/summary').then((response) => response.data);
export const fetchCampaign = async (publicId) => apiAxios.get(`/campaigns/${publicId}`).then((response) => response.data);
export const fetchPreviewClone = async (publicId) => apiAxios.get(`/campaigns/${publicId}/preview-clones`).then((response) => response.data);
export const updateCampaign = async ({ publicId, data }) => apiAxios.put(`/campaigns/${publicId}`, data).then((response) => response.data);
export const patchCampaign = ({ publicId, data }) => apiAxios.patch(`/campaigns/${publicId}`, data).then((response) => response.data);
export const patchCampaignPageDesign = ({ publicId, pageDesign }) => apiAxios
    .patch(`/campaigns/${publicId}/page-design`, { pageDesign })
    .then((response) => response.data);
export const deleteCampaign = (publicId) => apiAxios.delete(`/campaigns/${publicId}`).then((response) => response.data);
export const uploadCampaignImage = async ({ publicId, imageFile }) => {
    const formData = new FormData();
    formData.append('imageFile', imageFile);
    const response = await apiAxios.post(`/campaigns/${publicId}/uploads`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
};
export const fetchAnalytics = ({ publicId, from, to, entryPoint, excludeDevices, excludeBrowsers, }) => apiAxios
    .get(`/campaigns/${publicId}/analytics`, {
    params: {
        from: format(from, DATE_FORMAT_ISO),
        to: format(to, DATE_FORMAT_ISO),
        entryPoint,
        'excludeDevices[]': excludeDevices,
        'excludeBrowsers[]': excludeBrowsers,
    },
})
    .then((response) => response.data);
export const fetchEntryPoints = async (publicId) => apiAxios.get(`/campaigns/${publicId}/entry-points`).then((response) => response.data);
export const postEntryPoint = async ({ publicId, data }) => apiAxios
    .post(`/campaigns/${publicId}/entry-points`, data)
    .then((response) => response.data);
export const fetchTrackingLinks = async (publicId) => apiAxios.get(`/campaigns/${publicId}/tracking-links`).then((response) => response.data);
export const postTrackingLink = async ({ publicId, data }) => apiAxios
    .post(`/campaigns/${publicId}/tracking-links`, data)
    .then((response) => response.data);
export const reorderTrackingLinks = async ({ publicId, data }) => apiAxios
    .patch(`/campaigns/${publicId}/tracking-links/reorders`, data)
    .then((response) => response.data);
